import {
  CheckCircleIcon,
  CheckIcon,
  Download01Icon,
  Edit03Icon,
  InfoCircleIcon,
} from '@langchain/untitled-ui-icons';
import {
  Button,
  Checkbox,
  IconButton,
  LinearProgress,
  Modal,
  ModalClose,
  ModalDialog,
} from '@mui/joy';
import Alert from '@mui/joy/Alert';
import Box from '@mui/joy/Box';
import {
  ColumnDef,
  RowSelectionState,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '@/components/Breadcrumbs';
import { DataGridWithFooter } from '@/components/DataGrid';
import {
  emulateNativeClick,
  emulateNativeMiddleClick,
  getTableSelectColumnDef,
  useDataGridSizingLocalStorage,
} from '@/components/DataGrid.utils';
import { DateTimeRangePicker } from '@/components/DateTimeRangePicker/DateTimeRangePicker';
import EmptyState from '@/components/EmptyState';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { FeedbackChips } from '@/components/FeedbackChips';
import { RunStatusIcon } from '@/components/RunStatus';
import { LoadMoreButton } from '@/components/RunsTable/LoadMoreButton';
import {
  filterInputs,
  filterOutputs,
} from '@/components/RunsTable/utils/filterInputs';
import { TableCellTextOverflow } from '@/components/Table';
import { Tooltip } from '@/design-system';
import { GroupedTabs } from '@/design-system/components/GroupedTabs/GroupedTabs';
import { useStateFromSearchParams } from '@/hooks/useStateFromSearchParams';
import {
  useAnnotationQueue,
  useAnnotationQueueRunsInfinite,
  useAnnotationQueueSize,
  useAnnotationQueueTotalArchivedSize,
  useAnnotationQueueTotalSize,
  useDeleteAnnotationQueueRunsMutation,
  useOrganizationId,
} from '@/hooks/useSwr';
import { AnnotationQueueRunSchema } from '@/types/schema';
import {
  appAnnotationQueuesPath,
  appOrganizationPath,
} from '@/utils/constants';
import { capitalize, xCount } from '@/utils/stringUtils';

import { AnnotationQueueEmptyState } from '../AnnotationQueue';
import { RunType } from '../Run/components/RunType';
import { useChartTimeFilter } from '../SingleDashboard/hooks/useChartTimeFilter';
import { AnnotationQueueBulkViewSelectedRunsFooter } from './AnnotationQueueBulkViewSelectedRunsFooter';
import { DeleteAnnotationQueueRunsModal } from './DeleteAnnotationQueueRunsModal';
import {
  BULK_VIEW_COLUMN_SIZING_LS_KEY,
  BULK_VIEW_PAGE_SIZE,
} from './constants';
import { RunView } from './types';
import { useDownloadQueueRuns } from './utils/download';

const columnHelper = createColumnHelper<AnnotationQueueRunSchema>();

const COLUMNS_DEF: ColumnDef<AnnotationQueueRunSchema, any>[] = [
  getTableSelectColumnDef({ variant: 'virtual' }),

  columnHelper.accessor('status', {
    meta: { name: 'Status', overflow: false },
    size: 44,
    header: () => <CheckCircleIcon className="m-0.5 h-5 w-5" />,
    cell: ({ row }) => {
      return <RunStatusIcon run={row.original} />;
    },
  }),
  columnHelper.accessor('name', {
    header: 'Name',
    size: 200,
    cell: ({ row, table }) => {
      const { withTypeCol } =
        (table.options.meta as { withTypeCol?: boolean }) ?? {};
      return (
        <div className={withTypeCol ? 'flex flex-row gap-2' : ''}>
          {withTypeCol && <RunType run={row.original} />}
          <div className="font-medium">{row.original.name}</div>
        </div>
      );
    },
  }),
  columnHelper.accessor(
    (row) => (filterInputs(row.inputs) || row.inputs_preview)?.slice(0, 100),
    {
      id: 'inputs',
      header: 'Input',
      size: 500,
    }
  ),
  columnHelper.accessor(
    (row) =>
      (filterOutputs(row.outputs, row.inputs) || row.outputs_preview)?.slice(
        0,
        100
      ),
    {
      id: 'outputs',
      header: 'Output',
      size: 500,
      cell: ({ getValue }) => (
        <TableCellTextOverflow spacingVariant="wide">
          {getValue()}
        </TableCellTextOverflow>
      ),
    }
  ),
  {
    id: 'feedback_stats',
    header: 'Feedback',
    cell: ({ row }) => (
      <div className="absolute inset-0 mx-2 flex items-center gap-2 overflow-x-auto overflow-y-hidden py-4 no-scrollbar">
        <FeedbackChips
          feedbackStats={row.original.feedback_stats}
          feedbackSourceRunId={row.original.id}
          testIdPrefix="runs-table"
          className="shrink-0"
          showErrorCount={true}
        />
      </div>
    ),
    size: 700,
  },
];

const AnnotationQueueBulkViewPage = () => {
  'use no memo';
  const { annotationQueueId: queueId } = useParams();
  const queue = useAnnotationQueue(queueId);
  const organizationId = useOrganizationId();
  const navigate = useNavigate();
  const [runView, setRunView] = useStateFromSearchParams(
    'runView',
    RunView.Current
  );
  const showCompleted = runView === RunView.Completed;
  const queueRuns = useAnnotationQueueRunsInfinite(
    BULK_VIEW_PAGE_SIZE,
    queueId,
    {
      archived: showCompleted,
      include_stats: true,
    }
  );
  const queueSize = useAnnotationQueueSize(queueId);
  const totalQueueSize = useAnnotationQueueTotalSize(queueId);
  const totalCompletedSize = useAnnotationQueueTotalArchivedSize(
    showCompleted ? queueId : undefined
  );
  const [columnSizing, setColumnSizing] = useDataGridSizingLocalStorage(
    BULK_VIEW_COLUMN_SIZING_LS_KEY
  );
  const allRuns = useMemo(() => queueRuns.data?.flat() ?? [], [queueRuns.data]);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const { timeModel, setTimeModel } = useChartTimeFilter({});
  const [exportAllCompletedRuns, setExportAllCompletedRuns] = useState(false);

  const start_time = !exportAllCompletedRuns
    ? timeModel?.start_time
    : undefined;
  const end_time = !exportAllCompletedRuns ? timeModel?.end_time : undefined;
  const completedExportSize = useAnnotationQueueTotalArchivedSize(
    showCompleted ? queueId : undefined,
    { start_time, end_time }
  );

  const {
    trigger: triggerDownload,
    isMutating: isDownloading,
    error: downloadError,
  } = useDownloadQueueRuns(queueId, organizationId ?? undefined);

  const isNextPageValidating =
    queueRuns.isValidating &&
    queueRuns.size > 1 &&
    queueRuns.data?.length != queueRuns.size;

  const hasMoreRuns = useMemo(() => {
    let lastPageWithRuns: number | undefined = undefined;
    for (let i = queueRuns.data?.length ?? -1; i >= 0; i--) {
      if (queueRuns.data?.[i]?.length) {
        lastPageWithRuns = i;
        break;
      }
    }
    return lastPageWithRuns != null
      ? (queueRuns?.data?.[lastPageWithRuns]?.headers?.['x-pagination-total'] ??
          0) > allRuns.length
      : false;
  }, [queueRuns.data, allRuns.length]);

  const [selectAllRuns, setSelectAllRuns] = useState(false);
  const [rowSelection, _setRowSelection] = useState<RowSelectionState>({});

  const setRowSelection = useCallback(
    (
      rowSelectionUpdater:
        | RowSelectionState
        | ((prev: RowSelectionState) => RowSelectionState)
    ) => {
      const newState =
        typeof rowSelectionUpdater === 'function'
          ? rowSelectionUpdater(rowSelection)
          : rowSelectionUpdater;
      if (Object.values(newState).filter(Boolean).length === 0) {
        setSelectAllRuns(false);
      }
      _setRowSelection(rowSelectionUpdater);
    },
    [rowSelection]
  );

  useEffect(() => {
    if (selectAllRuns) {
      setRowSelection({
        ...(queueRuns.data
          ?.at(-1)
          ?.map((run) => run.id)
          .reduce((acc, id) => {
            acc[id] = true;
            return acc;
          }, {} as RowSelectionState) ?? {}),
        ...rowSelection,
      });
    }
  }, [queueRuns.data?.length]);

  const columns = useMemo(() => {
    if (showCompleted) {
      const dummyColumn = columnHelper.display({
        header: '',
        size: 42,
        id: 'dummy',
      });
      return [dummyColumn, ...COLUMNS_DEF.slice(1)];
    }
    return COLUMNS_DEF;
  }, [showCompleted]);

  const table = useReactTable({
    data: allRuns,
    columns: columns,
    state: {
      rowSelection,
      columnSizing,
    },
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange: setColumnSizing,
    getRowId: (row) => row.id,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),

    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableSorting: false,
    enableExpanding: false,
    manualPagination: true,
  });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const deleteAnnotationQueueRunsMutation =
    useDeleteAnnotationQueueRunsMutation(queueId, {
      onSuccess: () => {
        queueRuns.mutate();
        queueSize.mutate();
        totalQueueSize.mutate();
        setRowSelection({});
        setDeleteModalOpen(false);
        setSelectAllRuns(false);
      },
    });

  const excludedRuns = useMemo(
    () => allRuns.filter((run) => !rowSelection[run.id]).map((run) => run.id),
    [allRuns, rowSelection]
  );
  const onDeleteRuns = useCallback(() => {
    const selectedRuns = allRuns.filter((run) => rowSelection[run.id]);
    deleteAnnotationQueueRunsMutation.trigger({
      json: {
        run_ids: !selectAllRuns ? selectedRuns.map((run) => run.id) : undefined,
        delete_all: selectAllRuns,
        exclude_run_ids: selectAllRuns ? excludedRuns : undefined,
      },
    });
  }, [
    allRuns,
    deleteAnnotationQueueRunsMutation,
    selectAllRuns,
    excludedRuns,
    rowSelection,
  ]);

  const numRunsSelected = useMemo(() => {
    if (!selectAllRuns) {
      return Object.values(rowSelection).filter(Boolean).length;
    } else {
      return (
        (totalQueueSize.data?.size ?? allRuns.length) - excludedRuns.length
      );
    }
  }, [
    allRuns.length,
    excludedRuns.length,
    rowSelection,
    selectAllRuns,
    totalQueueSize.data?.size,
  ]);

  const allVisibleRunsSelected = useMemo(() => {
    return queueRuns.data?.every((page) =>
      page.every((run) => rowSelection[run.id])
    );
  }, [rowSelection, queueRuns.data]);

  const onRowClick = useCallback(
    ({
      row,
      event,
      emulateClick,
    }:
      | {
          row: AnnotationQueueRunSchema;
        } & (
          | {
              emulateClick: typeof emulateNativeMiddleClick;
              event: undefined;
            }
          | {
              emulateClick: typeof emulateNativeClick;
              event: MouseEvent;
            }
        )) => {
      if (row) {
        const rowIndex = allRuns.findIndex((r) => r.id === row.id);
        const targetUrl = showCompleted
          ? row.app_path
          : `/${appOrganizationPath}/${organizationId}/${appAnnotationQueuesPath}/${queueId}?runIndex=${rowIndex}`;

        const emulateClickResult = event
          ? emulateClick(targetUrl, event)
          : emulateClick(targetUrl);

        if (!emulateClickResult) {
          navigate(targetUrl);
        }
      }
    },
    [allRuns, navigate, organizationId, queueId, showCompleted]
  );

  const queueSizeCount = useMemo(() => {
    return showCompleted && totalCompletedSize.data
      ? totalCompletedSize.data?.size
      : queueSize.data?.size;
  }, [showCompleted, totalCompletedSize, queueSize]);

  const numHiddenRuns = useMemo(
    () => (totalQueueSize.data?.size ?? 0) - (queueSize.data?.size ?? 0),
    [totalQueueSize.data?.size, queueSize.data?.size]
  );
  const shouldShowSelectAll = useMemo(
    () =>
      !!(
        !selectAllRuns &&
        allVisibleRunsSelected &&
        (hasMoreRuns || numHiddenRuns > 0)
      ),
    [selectAllRuns, allVisibleRunsSelected, hasMoreRuns, numHiddenRuns]
  );

  if (queue.isLoading) {
    return <LinearProgress />;
  } else if (!queue.data) {
    return (
      <Box className="m-4">
        <Alert variant="outlined" color="danger">
          404: This queue does not exist
        </Alert>
      </Box>
    );
  }
  return (
    <div className="relative flex h-full flex-col overflow-y-auto overflow-x-visible">
      <div className="flex flex-col px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Edit03Icon className="h-5 w-5 text-quaternary" />
              <div className="text-xl font-medium">{queue.data.name}</div>
            </div>
            <GroupedTabs
              value={runView}
              options={[
                {
                  value: RunView.Current,
                  display: capitalize(RunView.Current),
                },
                {
                  value: RunView.Completed,
                  display: capitalize(RunView.Completed),
                },
              ]}
              onChange={setRunView}
            />
            {queueSizeCount != null && (
              <div className="text-base font-normal text-quaternary">
                {xCount('run', queueSizeCount)}
              </div>
            )}
          </div>
          {showCompleted && totalCompletedSize.data?.size ? (
            <Tooltip title="Download completed runs and feedback to a CSV">
              <IconButton
                size="sm"
                onClick={() => setDownloadModalOpen(true)}
                variant="solid"
              >
                <Download01Icon className="h-4 w-4" />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
        {queue.data.description && (
          <div className="pl-7 text-sm text-disabled">
            {queue.data.description}
          </div>
        )}
      </div>
      {queueRuns.isValidating ? (
        <LinearProgress size="sm" />
      ) : (
        <div className="h-[4px]" />
      )}
      <div className="h-full w-full overflow-x-auto">
        <DataGridWithFooter
          virtual={true}
          table={table}
          isLoading={false}
          onClick={(row, event) =>
            onRowClick({
              row,
              event: event.nativeEvent,
              emulateClick: emulateNativeClick,
            })
          }
          onMiddleClick={(row) => {
            onRowClick({
              row,
              event: undefined,
              emulateClick: emulateNativeMiddleClick,
            });
          }}
          footer={
            allRuns.length ? (
              <>
                {hasMoreRuns && (
                  <div className="flex flex-col items-center border-t border-secondary">
                    <LoadMoreButton
                      isInitialLoad
                      isLoading={isNextPageValidating}
                      isVirtual={true}
                      onClick={() => {
                        queueRuns.setSize((size) => size + 1);
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              queueId &&
              !queueRuns.isLoading &&
              (showCompleted ? (
                <div
                  className={'mr-0 mt-0 flex h-[700px] flex-col items-start'}
                >
                  <div className="flex h-full w-full flex-col items-center justify-center gap-5">
                    <EmptyState
                      title="No completed runs"
                      description={`Click "Done" when you're done reviewing a run to move it to the completed runs section.`}
                      icon={<CheckIcon />}
                    />

                    {organizationId && (
                      <Button
                        variant="outlined"
                        color="neutral"
                        onClick={() => {
                          navigate(
                            `/${appOrganizationPath}/${organizationId}/${appAnnotationQueuesPath}/${queueId}`
                          );
                        }}
                      >
                        Start reviewing runs
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <AnnotationQueueEmptyState
                  hideHeader
                  annotationQueueName={queue.data.name}
                  annotationQueueId={queueId}
                  organizationId={organizationId}
                  runRuleId={queue.data.run_rule_id}
                  className="mr-0 mt-0 h-[700px]"
                />
              ))
            )
          }
        />
      </div>
      {queueSize.data && totalQueueSize.data && (
        <DeleteAnnotationQueueRunsModal
          deleteModalOpen={deleteModalOpen}
          setDeleteModalOpen={setDeleteModalOpen}
          numRunsSelected={numRunsSelected}
          numHiddenRuns={numHiddenRuns}
          onDeleteRuns={onDeleteRuns}
          deleteMutating={deleteAnnotationQueueRunsMutation.isMutating}
          deleteError={deleteAnnotationQueueRunsMutation.error}
          totalQueueSize={totalQueueSize.data.size}
          queueSize={queueSize.data.size}
        />
      )}
      {numRunsSelected > 0 &&
        totalQueueSize.data &&
        queueSize.data &&
        !showCompleted && (
          <AnnotationQueueBulkViewSelectedRunsFooter
            shouldShowSelectAll={shouldShowSelectAll}
            setDeleteModalOpen={setDeleteModalOpen}
            totalQueueSize={totalQueueSize.data.size}
            setSelectAllRuns={setSelectAllRuns}
            numRunsSelected={numRunsSelected}
          />
        )}
      <Modal
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
      >
        <ModalDialog>
          <ModalClose />
          <div className="flex w-[450px] flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="text-md font-medium">
                Download completed runs and feedback to a CSV
              </div>
            </div>
            <Checkbox
              label="Export all completed runs"
              checked={exportAllCompletedRuns}
              onChange={() =>
                setExportAllCompletedRuns(!exportAllCompletedRuns)
              }
            />
            {!exportAllCompletedRuns && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="text-xs text-quaternary">
                    Choose time range
                  </div>
                  <Tooltip title={`Based on time marked "Done"`} side="right">
                    <div className="flex items-center gap-1">
                      <InfoCircleIcon className="h-4 w-4" />
                    </div>
                  </Tooltip>
                </div>
                <DateTimeRangePicker
                  value={timeModel}
                  onChange={setTimeModel}
                />
              </div>
            )}
            <div className="flex gap-3 self-end">
              <Button
                variant="solid"
                color="primary"
                disabled={
                  !organizationId ||
                  !queueId ||
                  isDownloading ||
                  completedExportSize.isLoading
                }
                loading={isDownloading || completedExportSize.isLoading}
                onClick={async () => {
                  if (organizationId && queueId) {
                    await triggerDownload({
                      start_time,
                      end_time,
                    });
                  }
                }}
              >
                Download {xCount('run', completedExportSize.data?.size ?? 0)}
              </Button>
            </div>
            {downloadError?.message && (
              <ExpandableErrorAlert error={downloadError.message} />
            )}
          </div>
        </ModalDialog>
      </Modal>
    </div>
  );
};

export default function Page() {
  return (
    <div className="h-screen pb-10 pt-3">
      <div className="px-4">
        <Breadcrumbs />
      </div>
      <AnnotationQueueBulkViewPage />
    </div>
  );
}
